import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const PublicWalkinApply = () => {
  const { token } = useParams();

  const [drive, setDrive] = useState(null);
  const [isClosed, setIsClosed] = useState(false);
  const [closedMessage, setClosedMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    position_interested: '',
    notes: '',
    resume_url: ''
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [resumePreview, setResumePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [confirmationData, setConfirmationData] = useState(null);

  const getApiUrl = () => {
    if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
      return '/api';
    }
    return import.meta.env.VITE_API_URL || '/api';
  };
  const API_URL = getApiUrl();

  useEffect(() => {
    const fetchDriveInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Fetching walk-in drive info for token:', token, 'via URL:', `${API_URL}/walkin-apply/${token}`);
        const res = await axios.get(`${API_URL}/walkin-apply/${token}`);
        console.log('Walk-in drive info response:', res.data);
        if (res.data.success && res.data.data) {
          const driveData = res.data.data.drive;
          setDrive(driveData);
          if (res.data.data.is_closed) {
            setIsClosed(true);
            setClosedMessage(res.data.data.message || 'This hiring drive is closed.');
          }
        } else {
          setError(res.data.message || 'Hiring drive not found or QR link expired.');
        }
      } catch (err) {
        console.error('Failed to load drive:', err);
        setError(err.response?.data?.message || 'Invalid or expired drive link.');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchDriveInfo();
    }
  }, [token, API_URL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowed.includes(file.type) && !['pdf', 'doc', 'docx'].includes(ext)) {
        setSubmitError('Please upload a PDF or Word document (.pdf, .doc, .docx)');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setSubmitError('Resume file size must be less than 5MB');
        return;
      }
      setResumeFile(file);
      setResumePreview(file.name);
      setSubmitError(null);
    }
  };

  const uploadResume = async (file) => {
    console.log('Uploading resume to:', `${API_URL}/walkin-apply/upload`);
    const uploadData = new FormData();
    uploadData.append('file', file);
    const res = await axios.post(`${API_URL}/walkin-apply/upload`, uploadData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    console.log('Resume upload response:', res.data);
    return res.data.data?.url || res.data.url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    if (!formData.name.trim() || !formData.phone.trim() || !formData.position_interested.trim()) {
      setSubmitError('Please fill in your Name, Phone Number, and Position Interested');
      setSubmitting(false);
      return;
    }

    try {
      let uploadedUrl = formData.resume_url;

      if (resumeFile) {
        setUploading(true);
        try {
          uploadedUrl = await uploadResume(resumeFile);
        } catch (upErr) {
          console.error('Resume upload failed:', upErr);
        } finally {
          setUploading(false);
        }
      }

      const payload = {
        ...formData,
        resume_url: uploadedUrl
      };

      console.log('Submitting candidate application to:', `${API_URL}/walkin-apply/${token}`, payload);
      const res = await axios.post(`${API_URL}/walkin-apply/${token}`, payload);
      console.log('Application submission response:', res.data);

      if (res.data.success && res.data.data) {
        setConfirmationData(res.data.data);
      } else {
        setSubmitError(res.data.message || res.data.error || 'Failed to submit application.');
      }
    } catch (err) {
      console.error('Application submission error:', err);
      setSubmitError(err.response?.data?.message || err.response?.data?.error || 'An error occurred while submitting your application.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{drive ? `Apply - ${drive.title}` : 'Walk-in Hiring Application'} | InChi Solutions</title>
        <meta name="description" content="Fast on-site walk-in hiring registration and instant interview panel assignment" />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col justify-between">
        <Header />

        <main className="flex-1 pt-20 pb-16 px-4">
          <div className="max-w-2xl mx-auto pt-4">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-24 bg-card rounded-3xl border border-border shadow-sm">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-sm font-semibold text-muted-foreground">Loading walk-in drive details...</p>
              </div>
            ) : error ? (
              <div className="p-10 text-center bg-card rounded-3xl border border-error/20 text-error shadow-sm">
                <div className="w-16 h-16 rounded-full bg-error/10 text-error flex items-center justify-center mx-auto mb-4">
                  <Icon name="AlertCircle" size={36} />
                </div>
                <h2 className="text-2xl font-bold mb-2">Drive Not Available</h2>
                <p className="text-sm text-muted-foreground mb-6">{error}</p>
                <Link to="/job-marketplace" className="inline-block px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-xs">
                  Browse Online Job Openings
                </Link>
              </div>
            ) : isClosed ? (
              <div className="p-10 text-center bg-card rounded-3xl border border-border shadow-sm">
                <div className="w-16 h-16 rounded-full bg-muted text-muted-foreground flex items-center justify-center mx-auto mb-4">
                  <Icon name="Lock" size={32} />
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">{drive?.title}</h2>
                <span className="inline-block px-3 py-1 rounded-full bg-muted text-muted-foreground text-xs font-bold mb-4">
                  Drive Completed / Closed
                </span>
                <p className="text-sm text-muted-foreground mb-6">{closedMessage}</p>
                <Link to="/job-marketplace" className="inline-block px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-xs">
                  Explore Active Job Openings
                </Link>
              </div>
            ) : confirmationData ? (
              /* INSTANT CONFIRMATION & PANEL ASSIGNMENT SCREEN */
              <div className="bg-card border-2 border-success/30 rounded-3xl p-6 sm:p-10 shadow-2xl text-center animate-fade-in-up">
                {/* Success Icon with Glow */}
                <div className="w-20 h-20 rounded-full bg-success/15 text-success flex items-center justify-center mx-auto mb-5 shadow-lg shadow-success/20 animate-bounce">
                  <Icon name="CheckCircle2" size={44} />
                </div>

                <span className="inline-block px-4 py-1.5 rounded-full bg-success/10 text-success text-xs font-bold uppercase tracking-wider mb-2">
                  Application Confirmed
                </span>

                <h1 className="text-2xl sm:text-3xl font-extrabold text-foreground mb-2">
                  Welcome, {confirmationData.name}!
                </h1>
                <p className="text-sm text-muted-foreground mb-6">
                  You have been assigned to an interview panel via our live queue dispatcher.
                </p>

                {/* Big Panel Assignment Hero Card */}
                <div className="p-6 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-2 border-primary/30 rounded-3xl mb-6 shadow-inner">
                  <div className="text-xs font-bold text-primary uppercase tracking-widest mb-1">
                    Your Assigned Interview Station
                  </div>
                  <div className="text-4xl sm:text-5xl font-black text-primary my-2">
                    PANEL {confirmationData.panel_number}
                  </div>
                  <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-card border border-primary/20 text-xs font-bold text-foreground shadow-sm">
                    <Icon name="Clock" size={14} className="text-primary" />
                    <span>Queue Position: #{confirmationData.queue_position} in line</span>
                  </div>
                </div>

                {/* Candidate & Venue Details */}
                <div className="p-5 bg-muted/30 rounded-2xl border border-border text-left text-xs space-y-2 mb-6">
                  <div className="flex justify-between py-1 border-b border-border">
                    <span className="text-muted-foreground">Candidate Name:</span>
                    <span className="font-bold text-foreground">{confirmationData.name}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-border">
                    <span className="text-muted-foreground">Position Applied:</span>
                    <span className="font-bold text-primary">{confirmationData.position_interested}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-border">
                    <span className="text-muted-foreground">Hiring Event:</span>
                    <span className="font-semibold text-foreground">{confirmationData.drive_title}</span>
                  </div>
                  <div className="flex justify-between py-1 border-b border-border">
                    <span className="text-muted-foreground">Venue:</span>
                    <span className="font-semibold text-foreground">{confirmationData.venue}, {confirmationData.location}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-muted-foreground">Reference Token:</span>
                    <span className="font-mono text-muted-foreground">{confirmationData.applicant_id.substring(0, 12)}...</span>
                  </div>
                </div>

                {/* On-site Instructions */}
                <div className="p-4 bg-primary/5 rounded-2xl border border-primary/15 text-xs text-left mb-6 text-foreground flex items-start gap-3">
                  <Icon name="Info" size={18} className="text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="block font-bold mb-0.5">What to do next:</strong>
                    Please walk directly to the <strong>Panel {confirmationData.panel_number}</strong> waiting desk. The interviewer has received your profile and will call your name shortly.
                  </div>
                </div>

                <Button
                  variant="default"
                  size="lg"
                  className="w-full font-bold shadow-lg"
                  onClick={() => window.print()}
                >
                  <Icon name="Printer" size={16} className="mr-2" /> Save / Print Confirmation
                </Button>
              </div>
            ) : (
              /* APPLICATION FORM */
              <div className="bg-card border border-border rounded-3xl p-6 sm:p-10 shadow-xl">
                {/* Header info */}
                <div className="pb-6 mb-6 border-b border-border">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-2">
                    <Icon name="Sparkles" size={13} />
                    Walk-in Hiring Application
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-extrabold text-foreground mb-1">
                    {drive?.title}
                  </h1>
                  {drive?.client_name && (
                    <p className="text-sm font-semibold text-primary mb-3">
                      Hiring for {drive.client_name}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mt-3">
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-muted/50 border border-border">
                      <Icon name="MapPin" size={13} /> {drive?.venue}, {drive?.location}
                    </span>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-muted/50 border border-border">
                      <Icon name="Calendar" size={13} /> {drive?.drive_date_start} to {drive?.drive_date_end}
                    </span>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10 text-primary font-semibold">
                      <Icon name="Zap" size={13} /> Instant Panel Assignment
                    </span>
                  </div>

                  {drive?.role_requirements && (
                    <div className="mt-4 p-3 bg-muted/30 rounded-xl border border-border text-xs text-muted-foreground leading-relaxed">
                      <strong className="text-foreground">Open Roles / Requirements:</strong> {drive.role_requirements}
                    </div>
                  )}
                </div>

                {submitError && (
                  <div className="p-4 mb-6 rounded-2xl bg-error/10 border border-error/20 text-error text-xs flex items-center gap-2">
                    <Icon name="AlertCircle" size={18} className="flex-shrink-0" />
                    <span>{submitError}</span>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-foreground block mb-1">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="e.g. Rahul Sharma"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 shadow-sm"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-foreground block mb-1">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="+91 9876543210"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 shadow-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold text-foreground block mb-1">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="your.email@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 shadow-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-foreground block mb-1">Position / Role Interested *</label>
                    <input
                      type="text"
                      name="position_interested"
                      required
                      placeholder="e.g. Fullstack Developer, QA Engineer, HR Executive"
                      value={formData.position_interested}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 shadow-sm"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-foreground block mb-1">Upload Resume (PDF / Word, Max 5MB)</label>
                    {resumePreview ? (
                      <div className="flex items-center justify-between p-3.5 border border-border rounded-xl bg-muted/20">
                        <div className="flex items-center gap-2 overflow-hidden">
                          <Icon name="FileText" size={20} className="text-primary flex-shrink-0" />
                          <span className="text-xs font-semibold text-foreground truncate">{resumePreview}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => { setResumeFile(null); setResumePreview(null); }}
                          className="text-error hover:text-error/80 text-xs font-bold"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary hover:bg-muted/10 transition-colors">
                        <Icon name="UploadCloud" size={24} className="text-muted-foreground mb-1" />
                        <span className="text-xs text-muted-foreground font-medium">Tap to attach resume document</span>
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                        />
                      </label>
                    )}
                  </div>

                  <div>
                    <label className="text-xs font-bold text-foreground block mb-1">Key Experience & Notes (Optional)</label>
                    <textarea
                      name="notes"
                      rows={2}
                      placeholder="Years of experience, core skills, notice period..."
                      value={formData.notes}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-xs focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none shadow-sm"
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    disabled={submitting || uploading}
                    className="w-full py-4 font-extrabold text-base shadow-xl mt-4"
                  >
                    {submitting || uploading ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>{uploading ? 'Uploading Resume...' : 'Assigning Interview Panel...'}</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <span>Submit Application & Get Assigned Panel</span>
                        <Icon name="ArrowRight" size={18} />
                      </div>
                    )}
                  </Button>

                  <p className="text-[11px] text-center text-muted-foreground mt-2">
                    ⚡ No account or login required. Submitting will immediately assign you to the shortest interview queue.
                  </p>
                </form>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PublicWalkinApply;
